import * as THREE from 'three';

export interface SplatScene {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  resize: () => void;
  dispose: () => void;
}

// Panel layout constants — must match the panel.ts CSS so the off-axis
// projection frames the look-at center within the visible (non-panel) region.
// Horizontally it lands dead-center of that region; vertically on mobile it's
// biased a touch ABOVE center (see MOBILE_PIVOT_LIFT_FRAC).
const DESKTOP_PANEL_RESERVED_PX = 504; // 480px panel + 24px right gap
// Collapsed mobile sheet peek height in px — must match COLLAPSED_PEEK_PX in
// panel/panel.ts so the camera frames around the resting (collapsed) sheet.
const MOBILE_PANEL_PEEK_PX = 210;
// Fraction of the visible (above-sheet) strip by which the orbit pivot is
// raised ABOVE that strip's center on mobile. anchor === camera.target for
// every hotspot (see tour.json), so the numbered chip sits on the pivot;
// lifting the pivot keeps the point you rotate around in the upper part of
// the visible area. Tune by feel.
const MOBILE_PIVOT_LIFT_FRAC = 0.06;
const MOBILE_BREAKPOINT_PX = 768;
const DESKTOP_FOV = 50;
const MOBILE_FOV = 80;

// Force DPR=1 regardless of the device's actual pixel ratio. Splats are
// alpha-blended quads with massive overdraw — fragment cost scales with the
// framebuffer pixel count, and on retina (DPR=2) or 4K external (DPR up to
// 2.5) that's 4-6× more pixels for ~zero visual gain. Gaussian splats are
// soft-edged by construction, so the supersampling that DPR>1 buys for
// hard-edged geometry does nothing here: the splat IS already an
// anti-aliased ellipse and the browser's bilinear upscale of the 1:1
// canvas is imperceptible.
//
// UI text/icons render via HTML/CSS at native DPR independently — this only
// affects the splat scene. The previous "render at native DPR" config was
// halving FPS on Mac Studio retina; verified by the ?lowdpr diagnostic flag
// before being made the permanent setting.
function pickPixelRatio(): number {
  return 1.0;
}

export function createSplatScene(canvas: HTMLCanvasElement): SplatScene {
  // ?nooffaxis URL flag disables setViewOffset for perf diagnosis. With it
  // active, the lookAt center lands at canvas-center (behind the panel) but
  // the camera projection is a normal symmetric frustum — same as the
  // reference CASUA viewer uses. Tells us if our wider virtual frustum is
  // costing fragment/vertex work.
  const noOffAxis = new URLSearchParams(location.search).has('nooffaxis');
  // antialias MUST be false for splat rendering. Gaussian splats are
  // alpha-blended quads with heavy overdraw — MSAA gives ~zero visual benefit
  // (the gaussians are already soft) but multiplies fragment cost. On retina
  // (DPR 2) + Apple Silicon TBDR, MSAA + heavy overdraw was halving FPS on Mac
  // Studio. mkkellogg's own self-driven Viewer creates its renderer with
  // `antialias: false` for this reason; since we use DropInViewer + supply the
  // renderer ourselves, we have to match that explicitly.
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false,
    powerPreference: 'high-performance'
  });
  renderer.setPixelRatio(pickPixelRatio());
  renderer.setSize(window.innerWidth, window.innerHeight, false);
  renderer.setClearColor(0xa39e95, 1);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xa39e95);

  const camera = new THREE.PerspectiveCamera(
    DESKTOP_FOV,
    window.innerWidth / window.innerHeight,
    0.1,
    2000
  );
  camera.position.set(50, 50, 50);
  camera.lookAt(0, 0, 0);

  // Off-axis projection so the look-at center lands in the visible non-panel
  // area, not the canvas center. setViewOffset(fullW,fullH, offX,offY, w,h)
  // tells Three to compute the projection for the virtual (fullW × fullH)
  // frame and then crop to the (w × h) slice starting at (offX, offY).
  // camera.aspect must be the FULL virtual aspect, not the slice aspect.
  const applyLayout = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    renderer.setPixelRatio(pickPixelRatio());
    renderer.setSize(w, h, false);
    if (noOffAxis) {
      // Diagnostic mode: standard symmetric projection, no panel offset.
      camera.fov = w < MOBILE_BREAKPOINT_PX ? MOBILE_FOV : DESKTOP_FOV;
      camera.aspect = w / h;
      camera.clearViewOffset();
    } else if (w < MOBILE_BREAKPOINT_PX) {
      const panelH = MOBILE_PANEL_PEEK_PX;
      const fullH = h + panelH;
      // Slide the crop window down within the virtual frame so the fixed
      // look-at center sits higher in the slice (offsetY = panelH + lift ⇒
      // pivot rises by `lift` px). Pure translation of the slice — fullH and
      // fov are untouched, so this only repositions the framing, never
      // rescales it.
      const lift = Math.round((h - panelH) * MOBILE_PIVOT_LIFT_FRAC);
      camera.fov = MOBILE_FOV;
      camera.aspect = w / fullH;
      camera.setViewOffset(w, fullH, 0, panelH + lift, w, h);
    } else {
      const panelW = Math.min(DESKTOP_PANEL_RESERVED_PX, Math.round(w * 0.45));
      const fullW = w + panelW;
      camera.fov = DESKTOP_FOV;
      camera.aspect = fullW / h;
      camera.setViewOffset(fullW, h, panelW, 0, w, h);
    }
    camera.updateProjectionMatrix();
    renderer.render(scene, camera);
  };
  applyLayout();
  window.addEventListener('resize', applyLayout);

  const dispose = () => {
    window.removeEventListener('resize', applyLayout);
    renderer.dispose();
  };

  return { scene, camera, renderer, resize: applyLayout, dispose };
}
